import React, { useEffect } from "react";
import Icon from "@mdi/react";
import {
  mdiClose,
  mdiAlertCircleOutline,
  mdiCalendar,
  mdiAccount,
  mdiWarehouse,
  mdiPackageVariantClosed,
} from "@mdi/js";
import { DisputedItemModel } from "../model/quarantine_model";
import dayjs from "dayjs";
import DeviceIdsViewer from "../common-components/DeviceIdsViewer";

interface QuarantineItemDetailsModalProps {
  item: DisputedItemModel | null;
  isOpen: boolean;
  onClose: () => void;
}

const QuarantineItemDetailsModal: React.FC<QuarantineItemDetailsModalProps> = ({
  item,
  isOpen,
  onClose,
}) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  if (!isOpen || !item) return null;

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(value);
  };

  const statusStyles: Record<string, string> = {
    open: "bg-yellow-100 text-yellow-700 border-yellow-200",
    released: "bg-green-100 text-green-700 border-green-200",
    returned: "bg-blue-100 text-blue-700 border-blue-200",
    scrapped: "bg-red-100 text-red-700 border-red-200",
  };

  return (
    <div
      id="warehouse-quarantine-details-modal-root"
      className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-60 animate-in fade-in duration-200"
    >
      <div className="flex min-h-screen items-start justify-center p-4">
        <div className="relative w-full max-w-3xl bg-white rounded-xl shadow-2xl border animate-in zoom-in-95 duration-200 wh-modal-xs">
          {/* Header */}
          <div className="flex flex-col space-y-1.5 p-6 border-b">
            <button
              id="warehouse-quarantine-details-modal-close-icon-button"
              onClick={onClose}
              className="absolute right-4 top-4 p-2 rounded-full hover:bg-gray-100 transition-colors text-gray-500 hover:text-gray-700"
            >
              <Icon path={mdiClose} size={0.8} />
            </button>
            <div className="flex flex-wrap items-center gap-3">
              <h2 className="text-xl font-bold text-gray-900">
                {item.item_name}
              </h2>
              <div className="inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-2.5 py-0.5 text-xs font-mono font-medium text-gray-600">
                {item.inventory_item_code}
              </div>
              <span
                className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${
                  statusStyles[item.status?.toLowerCase()] ||
                  "bg-gray-100 text-gray-700"
                }`}
              >
                {item.status?.charAt(0).toUpperCase() + item.status?.slice(1)}
              </span>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 space-y-8">
            {/* Main Info Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              <div className="space-y-1 bg-gray-50 p-3 rounded-lg border border-gray-100">
                <p className="text-xs font-semibold text-gray-500 flex items-center gap-1">
                  <Icon
                    path={mdiPackageVariantClosed}
                    size={0.5}
                    className="text-orange-600"
                  />
                  Order ID
                </p>
                <p className="font-mono text-sm text-gray-900 font-bold">
                  {item.inward_order_id}
                </p>
              </div>

              <div className="space-y-1 bg-gray-50 p-3 rounded-lg border border-gray-100">
                <p className="text-xs font-semibold text-gray-500 flex items-center gap-1">
                  <Icon
                    path={mdiWarehouse}
                    size={0.5}
                    className="text-blue-600"
                  />
                  Warehouse
                </p>
                <p className="text-sm text-gray-900 font-bold">
                  {item.warehouse_name}
                </p>
              </div>
              <div className="space-y-1 bg-gray-50 p-3 rounded-lg border border-gray-100">
                <p className="text-xs font-semibold text-gray-500 flex items-center gap-1">
                  Disputed Qty
                </p>
                <p className="text-lg font-bold text-gray-900">
                  {item.disputed_physical_quantity}
                </p>
              </div>
              <div className="space-y-1 bg-gray-50 p-3 rounded-lg border border-gray-100">
                <p className="text-xs font-semibold text-gray-500 flex items-center gap-1">
                  Disputed Rate
                </p>
                <p className="text-lg font-bold text-gray-900">
                  {formatCurrency(item.disputed_invoice_rate || 0)}
                </p>
              </div>
              <div className="space-y-1 bg-gray-50 p-3 rounded-lg border border-gray-100">
                <p className="text-xs font-semibold text-gray-500 flex items-center gap-1">
                  Total Value
                </p>
                <p className="text-lg font-bold text-gray-900">
                  {formatCurrency(item.disputed_total_value)}
                </p>
              </div>
            </div>

            {/* Comparison Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-slate-50 rounded-xl border border-slate-100">
              <div className="space-y-1">
                <p className="font-bold">PO Baseline</p>
                <div className="flex items-baseline gap-2">
                  <span className="text-sm font-bold">
                    {item.po_quantity} Qty
                  </span>
                  <span className="text-xs">
                    @ {formatCurrency(item.po_rate)} / uhnit
                  </span>
                </div>
              </div>

              <div className="space-y-1 border-l border-slate-200 pl-4">
                <p className="font-bold">Invoice Details</p>
                <div className="flex items-baseline gap-2">
                  <span className="text-sm font-bold">
                    {item.invoice_quantity} Qty
                  </span>
                  <span className="text-xs">
                    @ {formatCurrency(item.disputed_invoice_rate)} / unit
                  </span>
                </div>
              </div>

              <div className="space-y-1 border-l border-slate-200 pl-4">
                <p className="font-bold">Physical Receipt</p>
                <div className="flex items-baseline gap-2">
                  <span className="text-sm font-bold text-blue-700">
                    {item.disputed_physical_quantity} Qty
                  </span>
                  <span className="text-xs">Actually Counted</span>
                </div>
              </div>
            </div>

            {/* Dispute Details */}
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-1">
                <p className="text-sm font-bold text-gray-900 flex items-center gap-1">
                  <Icon
                    path={mdiAlertCircleOutline}
                    size={0.6}
                    className="text-red-500"
                  />
                  Dispute Reason
                </p>
                <div className="flex flex-wrap gap-1">
                  {item.dispute_types.map((type, idx) => (
                    <span
                      key={idx}
                      className="inline-flex items-center rounded-full border border-gray-200 bg-white px-2 py-0.5 font-semibold text-gray-700"
                    >
                      {type}
                    </span>
                  ))}
                </div>
              </div>
              <p className="text-sm text-gray-700">
                {item.remark || "No detail description provided."}
              </p>
            </div>

            {/* Device IDs if applicable */}
            {item.disputed_device_ids &&
              item.disputed_device_ids.length > 0 && (
                <DeviceIdsViewer
                  deviceIds={item.disputed_device_ids}
                  title="Quarantined Device IDs"
                />
              )}

            {/* Metadata */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
              <div className="flex items-center gap-3 rounded-xl">
                <div className="p-2 bg-white rounded-lg shadow-sm">
                  <Icon
                    path={mdiCalendar}
                    size={0.8}
                    className="text-gray-400"
                  />
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-400er">
                    Quarantined At
                  </p>
                  <p className="font-bold text-gray-900">
                    {item.created_at
                      ? dayjs(item.created_at).format("DD MMM YYYY, hh:mm a")
                      : "N/A"}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3 rounded-xl">
                <div className="p-2 bg-white rounded-lg shadow-sm">
                  <Icon
                    path={mdiAccount}
                    size={0.8}
                    className="text-gray-400"
                  />
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-400er">
                    Quarantined By
                  </p>
                  <p className="font-bold text-gray-900">
                    {item.created_by_name || "System"}
                  </p>
                </div>
              </div>
            </div>

            <hr />

            {item.resolved_at && (
              <React.Fragment>
                <div className="space-y-4">
                  <p className="text-sm font-bold text-gray-900 flex items-center gap-1">
                    <Icon
                      path={mdiAlertCircleOutline}
                      size={0.6}
                      className="text-red-500"
                    />
                    Remarks on Resolution
                  </p>
                  <p className="text-sm text-gray-700">
                    {item?.reason || "No detail description provided."}
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pb-2 text-sm">
                  <div className="flex items-center gap-3 bg-gray-50/50 p-4 rounded-xl">
                    <div className="p-2 bg-white rounded-lg shadow-sm">
                      <Icon
                        path={mdiCalendar}
                        size={0.8}
                        className="text-gray-400"
                      />
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-gray-400er">
                        Resolved At
                      </p>
                      <p className="font-bold text-gray-900">
                        {item.resolved_at
                          ? dayjs(item.resolved_at).format(
                              "DD MMM YYYY, hh:mm a",
                            )
                          : "N/A"}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 bg-gray-50/50 p-4 rounded-xl">
                    <div className="p-2 bg-white rounded-lg shadow-sm">
                      <Icon
                        path={mdiAccount}
                        size={0.8}
                        className="text-gray-400"
                      />
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-gray-400er">
                        Resolved By
                      </p>
                      <p className="font-bold text-gray-900">
                        {item.resolved_by_name || "System"}
                      </p>
                    </div>
                  </div>
                </div>
              </React.Fragment>
            )}
          </div>

          {/* Footer */}
          <div className="p-4 bg-gray-50 border-t flex justify-end gap-3 rounded-b-xl">
            <button
              id="warehouse-quarantine-details-modal-close-footer-button"
              onClick={onClose}
              className="px-6 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-semibold text-sm shadow-sm"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuarantineItemDetailsModal;
